import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useTranslatedToast } from '@/hooks/useTranslatedToast';

export interface UserDocument {
  id: string;
  user_id: string;
  file_name: string;
  original_file_name?: string;
  file_url: string;
  file_type: string;
  file_size: number;
  uploaded_at: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  version_number?: number;
  is_latest?: boolean;
}

export interface DuplicateFileInfo {
  id: string;
  file_name: string;
  version_number: number;
  file_size: number;
  created_at: string;
  file_type: string;
  is_latest: boolean;
}

export const useUserDocuments = () => {
  const [documents, setDocuments] = useState<UserDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useTranslatedToast();

  const fetchUserDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('user_documents')
        .select('*')
        .eq('is_active', true as any)
        .order('uploaded_at', { ascending: false });

      if (error) throw error;
      
      // Calculate version numbers for each file
      const documentsWithVersions = ((data as any) || []).map((doc: any) => {
        // Find all documents with the same original filename (or file_name as fallback)
        const compareFileName = doc.original_file_name || doc.file_name;
        const sameFileVersions = ((data as any) || [])
          .filter((d: any) => (d.original_file_name || d.file_name) === compareFileName)
          .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        
        // Assign version number (newest gets highest number)
        const versionIndex = sameFileVersions.findIndex((v: any) => v.id === doc.id);
        const version_number = sameFileVersions.length - versionIndex;
        const is_latest = versionIndex === 0;
        
        return {
          ...(doc as any),
          version_number,
          is_latest
        };
      });
      
      setDocuments(documentsWithVersions);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch documents';
      setError(errorMessage);
      toast.raw.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const checkForDuplicates = async (fileName: string): Promise<DuplicateFileInfo[]> => {
    try {
      const { data, error } = await supabase
        .from('user_documents')
        .select('id, file_name, original_file_name, file_size, created_at, file_type')
        .eq('original_file_name', fileName as any)
        .eq('is_active', true as any)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Add version_number and is_latest for compatibility with DuplicateFileDialog
      // Newest file (index 0) gets the highest version number
      return ((data as any) || []).map((doc: any, index: number) => ({
        ...(doc as any),
        file_name: doc.original_file_name || doc.file_name,
        version_number: data.length - index,
        is_latest: index === 0
      }));
    } catch (err) {
      console.error('Error checking duplicates:', err);
      return [];
    }
  };

  const uploadDocument = async (
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<UserDocument | null> => {
    try {
      setError(null);
      
      // Check file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('File size must be less than 10MB');
      }

      // Get current user for file path
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Upload file to Supabase Storage with consistent path structure
      const timestamp = Date.now();
      // Sanitize filename for URL safety, especially for Chinese characters
      const sanitizedFileName = file.name.replace(/[^\w\-_.]/g, '_');
      const fileName = `${timestamp}_${sanitizedFileName}`;
      const filePath = `documents/${user.id}/${fileName}`;

      console.log('Uploading to bucket: user-documents, path:', filePath);

      if (onProgress) onProgress(10);

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('user-documents')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw uploadError;
      }

      if (onProgress) onProgress(50);

      // Get the public URL
      // Store the file path instead of public URL for private bucket
      const fileUrl = `https://data.evee.ai/storage/v1/object/public/user-documents/${filePath}`;

      if (onProgress) onProgress(75);

      // Save document record to database
      const { data: documentData, error: dbError } = await supabase
        .from('user_documents')
        .insert({
          user_id: user.id,
          file_name: sanitizedFileName, // Sanitized filename matching storage path
          original_file_name: file.name, // Original filename for display
          file_url: fileUrl,
          file_type: file.type,
          file_size: file.size
        } as any)
        .select()
        .single();

      if (dbError) throw dbError;

      if (onProgress) onProgress(100);

      // Refresh documents list
      await fetchUserDocuments();
      
      toast.success('success.uploaded');
      return (documentData as any);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload document';
      setError(errorMessage);
      toast.raw.error(errorMessage);
      if (onProgress) onProgress(0); // Reset progress on error
      return null;
    }
  };

  const uploadDocumentVersion = async (
    file: File,
    action: 'version' | 'rename',
    newFileName?: string,
    onProgress?: (progress: number) => void
  ): Promise<UserDocument | null> => {
    try {
      setError(null);

      if (file.size > 10 * 1024 * 1024) {
        throw new Error('File size must be less than 10MB');
      }

      // Get current user for file path
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const finalFileName = action === 'rename' && newFileName ? newFileName : file.name;
      const timestamp = Date.now();
      // Sanitize the filename for storage path
      const sanitizedFileName = finalFileName.replace(/[^\w\-_.]/g, '_');
      const storageFileName = `${timestamp}_${sanitizedFileName}`;
      const filePath = `documents/${user.id}/${storageFileName}`;

      if (onProgress) onProgress(10);

      console.log('Uploading version to bucket: user-documents, path:', filePath);

      // Upload new file
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('user-documents')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Upload version error:', uploadError);
        throw uploadError;
      }

      if (onProgress) onProgress(50);

      // Store the file path instead of public URL for private bucket
      const fileUrl = `https://data.evee.ai/storage/v1/object/public/user-documents/${filePath}`;

      if (onProgress) onProgress(75);

      // Create new document (version or rename)
      const { data: documentData, error: dbError } = await supabase
        .from('user_documents')
        .insert({
          user_id: user.id,
          file_name: sanitizedFileName, // Sanitized filename matching storage path
          original_file_name: finalFileName, // Original filename for display
          file_url: fileUrl,
          file_type: file.type,
          file_size: file.size
        } as any)
        .select()
        .single();

      if (dbError) throw dbError;
      
      if (onProgress) onProgress(100);
      
      await fetchUserDocuments();
      const actionText = action === 'version' ? 'New version created' : 'File renamed';
      const displayFileName = newFileName || file.name;
      toast.raw.success(`${displayFileName} - ${actionText}`);
      return (documentData as any);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload document';
      setError(errorMessage);
      toast.raw.error(errorMessage);
      if (onProgress) onProgress(0); // Reset progress on error
      return null;
    }
  };

  const deleteDocument = async (documentId: string): Promise<boolean> => {
    try {
      setError(null);
      
      const { error } = await supabase
        .from('user_documents')
        .update({ is_active: false } as any)
        .eq('id', documentId as any);

      if (error) throw error;
      
      await fetchUserDocuments();
      toast.success('success.deleted');
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete document';
      setError(errorMessage);
      toast.raw.error(errorMessage);
      return false;
    }
  };

  const getFileVersions = async (fileName: string): Promise<UserDocument[]> => {
    try {
      const { data, error } = await supabase
        .from('user_documents')
        .select('*')
        .eq('original_file_name', fileName as any)
        .eq('is_active', true as any)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data as any) || [];
    } catch (err) {
      console.error('Error fetching file versions:', err);
      return [];
    }
  };

  useEffect(() => {
    fetchUserDocuments();
  }, []);

  const generateSignedDownloadUrl = useCallback(async (fileUrl: string): Promise<string | null> => {
    try {
      // Extract the file path from the full URL
      const urlParts = fileUrl.split('/storage/v1/object/public/user-documents/');
      if (urlParts.length < 2) {
        console.error('Invalid file URL format:', fileUrl);
        return null;
      }
      
      const filePath = urlParts[1];

      const { data, error } = await supabase.storage
        .from('user-documents')
        .createSignedUrl(filePath, 3600); // 1 hour expiry

      if (error) {
        console.error('Error creating signed URL:', error);
        return null;
      }

      return data.signedUrl;
    } catch (err) {
      console.error('Failed to generate signed URL:', err);
      return null;
    }
  }, []);

  return {
    documents,
    loading,
    error,
    fetchUserDocuments,
    uploadDocument,
    uploadDocumentVersion,
    deleteDocument,
    checkForDuplicates,
    getFileVersions,
    generateSignedDownloadUrl
  };
};